import { roleRoutes } from "../utils/roles";
import { HELPDESK_INCIDENTS_ENABLED, TICKETS_ENABLED } from "./featureFlags";

export const CLIENT_MANAGER_ROLE_ID = "66edc31513059690d19d6eaf";

// Módulos habilitados para la empresa del usuario (hooks/useTenantModules), tal
// como los pasan los callers en `options.tenantModules`, con el tipo de empresa
// al que pertenecen en `options.tenantType`. Solo cuentan si ese tipo es el del
// portal que se arma ('logistic' en /admin, 'company' en /home). El backend ya
// elige el tenant según el rol, así que esto es defensa en profundidad: módulos
// de otro tipo de empresa nunca habilitan un ítem de este portal. Cualquier otra
// cosa cuenta como "ninguno": un ítem condicionado a un módulo nunca se ofrece
// por defecto.
const tenantModulesOf = (options, portalTenantType) =>
  options?.tenantType === portalTenantType && Array.isArray(options?.tenantModules)
    ? options.tenantModules
    : [];

// "Rutificador" del portal de operación (/admin/adminRoute). El superadmin del
// sistema lo ve siempre y tal cual (sin marca): es quien lo evalúa y lo habilita.
// El operador (admin y superadmin logístico) lo ve solo si la plataforma le
// habilitó el módulo `rutificador`, marcado "Beta" porque todavía no crea rutas
// en el backend. Soporte y supervisor tienen menús propios y no lo llevan.
// Es curaduría de menú: el control de acceso es RequireTenantModule (routes.jsx).
const rutificadorAdminItems = (role, tenantModules) => {
  if (role === roleRoutes.systemSuperadmin) {
    return [{ label: "Rutificador", icon: "route", path: "/admin/adminRoute" }];
  }
  const isOperatorAdmin = role === roleRoutes.admin || role === roleRoutes.superadmin;
  if (isOperatorAdmin && tenantModules.includes("rutificador")) {
    return [{ label: "Rutificador", icon: "route", path: "/admin/adminRoute", badge: "Beta" }];
  }
  return [];
};

// Ítem de "Tickets" del sidebar, o nada si el flag está apagado. Se arma una
// sola vez porque aparece en TRES menús distintos (admin, superadmin logístico
// y soporte) y los tres tienen que encenderse y apagarse juntos: si a uno se le
// olvidara el flag, ese rol vería una entrada que lleva a una ruta que con el
// flag apagado ya no existe.
const ticketsMenuItem = TICKETS_ENABLED
  ? [{ label: "Tickets", icon: "confirmation_number", path: "/admin/tickets" }]
  : [];

// Ítem de "Atención e incidencias", o nada si el flag está apagado. Mismo
// motivo que ticketsMenuItem: aparece en TRES menús (admin y superadmin
// logístico, soporte y supervisor) que tienen que encenderse y apagarse juntos
// con la ruta. Va siempre justo debajo de "Mesa de ayuda", la bandeja clásica.
//
// `shortTitle` (opcional, solo este ítem por ahora): título del header bajo
// 768 px, donde "Atención e incidencias" no entra y se recorta (mockup W4:
// "[≡] Atención"). La clave va desambiguada porque las claves de i18n son el
// texto en español y "Atención" ya existe con el sentido de aviso ("Attention",
// "注意", en _missing.json): compartirla traduciría este título como
// "Attention". El español no tiene archivo de traducciones y mostraría la clave
// tal cual, por eso el texto visible viaja en `defaultValue`.
const helpdeskIncidentsMenuItem = HELPDESK_INCIDENTS_ENABLED
  ? [
      {
        label: "Atención e incidencias",
        icon: "headset_mic",
        path: "/admin/helpdesk/incidencias",
        shortTitle: {
          key: "Atención (título corto de la vista Atención e incidencias)",
          defaultValue: "Atención",
        },
      },
    ]
  : [];

// Vistas del portal de operación que el superadministrador del SISTEMA no usa:
// son la operación diaria de un courier (su trabajo es la plataforma). Se
// ocultan solo de SU menú — `admin` y el superadmin logístico comparten
// getAdminSections y no cambian.
// Ojo: esto es curaduría de menú, NO control de acceso. Las rutas siguen
// existiendo y son alcanzables por URL, que es lo que corresponde para este rol.
const RUTAS_OCULTAS_SYSTEM_SUPERADMIN = new Set([
  "/admin/dashboard",
  "/admin/torre-control",
  "/admin/recolecciones",
  "/admin/live-tracking",
  "/admin/live-routes",
  "/admin/inform",
  "/admin/delivery-quality",
  "/admin/pickupsView",
  "/admin/cobro",
  "/admin/pago-repartidores",
  "/admin/users",
  "/admin/contracts",
  "/admin/prospects",
  "/admin/incidents",
  "/admin/tickets",
  "/admin/helpdesk",
  "/admin/helpdesk/incidencias",
  "/admin/notifications",
]);

// ─────────────────────────────────────────────────────────────────────────────
// Secciones del sidebar según rol y aplicación seleccionada (solo admin/superadmin)
// ─────────────────────────────────────────────────────────────────────────────

// Sidebar unificado: una sola lista con TODAS las áreas (antes repartidas en el
// switcher "Aplicaciones" del header, ya eliminado). Orden operativo.
// Recibe el roleId del usuario para gatear ítems restringidos (p.ej. el grupo
// PLATAFORMA, solo para el superadmin del sistema) y los módulos habilitados de
// su empresa (p.ej. el Rutificador).
// Catálogo completo; getAdminSections lo filtra por rol antes de devolverlo.
const buildAdminSections = (role, tenantModules) => [
  {
    title: "OPERACIÓN",
    icon: "package_2",
    items: [
      { label: "Dashboard", icon: "space_dashboard", path: "/admin/dashboard" },
      { label: "Pedidos", icon: "list_alt", path: "/admin/pedidos" },
      { label: "Torre de control", icon: "dvr", path: "/admin/torre-control" },
      { label: "Recolecciones", icon: "event_repeat", path: "/admin/recolecciones" },
      // Paquetes cuya dirección no se pudo ubicar con precisión de calle y
      // número: quedan sin coordenada y por lo tanto FUERA de la ruta
      // optimizada. Va en OPERACIÓN y no en un grupo de configuración porque es
      // trabajo del día, no un ajuste: si nadie lo resuelve antes de que salga
      // el reparto, el bulto viaja sin parada en la secuencia.
      // `badgeKey` lo resuelve el sidebar contra el contador del backend.
      {
        label: "Direcciones por resolver",
        icon: "wrong_location",
        path: "/admin/direcciones-por-resolver",
        badgeKey: "pendingAddresses",
      },
      // "Rutificador" (/admin/adminRoute) — el superadmin del sistema y, con el
      // módulo habilitado para su empresa, el operador. Ver rutificadorAdminItems.
      ...rutificadorAdminItems(role, tenantModules),
      // "Recepción en bodega" se oculta del sidebar; la ruta /admin/recepcion-bodega
      // sigue existiendo, pero no se surfacea en el menú.
      // { label: "Recepción en bodega", icon: "warehouse", path: "/admin/recepcion-bodega" },
    ],
  },
  {
    title: "SEGUIMIENTO",
    icon: "my_location",
    items: [
      { label: "Seguimiento en vivo", icon: "sensors", path: "/admin/live-tracking" },
      { label: "En vivo", icon: "location_on", path: "/admin/live-routes" },
      // "En vivo (Nueva)" (/admin/live-routes-nueva, rediseño en evaluación) sale
      // del sidebar: era exclusivo del superadmin del sistema, y ese rol ya no
      // lleva SEGUIMIENTO. La ruta sigue existiendo para probarla por URL.
    ],
  },
  {
    title: "ANÁLISIS",
    icon: "bar_chart",
    items: [
      { label: "Forecasting", icon: "trending_up", path: "/admin/forecasting" },
      { label: "Crear informe", icon: "add_chart", path: "/admin/inform" },
      { label: "Calidad de entregas", icon: "verified", path: "/admin/delivery-quality" },
      { label: "Análisis de entregas", icon: "map", path: "/admin/delivery-analytics" },
      { label: "Retiros realizados", icon: "check_circle", path: "/admin/pickupsView" },
    ],
  },
  {
    title: "FACTURACIÓN",
    icon: "receipt_long",
    items: [
      // Separados en ago-2026: cobrar a comercios y pagar a la flota son dos
      // negocios distintos. "Facturación" (SII, documentos tributarios) será
      // un módulo aparte cuando exista.
      { label: "Notas de cobro", icon: "receipt", path: "/admin/cobro" },
      { label: "Pago a repartidores", icon: "wallet", path: "/admin/pago-repartidores" },
    ],
  },
  // FACTURACIÓN (legacy) se oculta del menú: "Pre-documentos" y "Tarifas Repartidores"
  // estaban mal hechos y no se usan. Las rutas siguen existiendo, pero no se
  // surfacean en el sidebar hasta que se rehagan.
  // {
  //   title: "FACTURACIÓN",
  //   icon: "receipt_long",
  //   items: [
  //     { label: "Pre-documentos", icon: "receipt", path: "/admin/billing/pre-documents" },
  //     { label: "Tarifas Repartidores", icon: "payments", path: "/admin/billing/driver-tariffs" },
  //   ],
  // },
  {
    title: "GESTIÓN",
    icon: "groups",
    items: [
      { label: "Usuarios", icon: "local_shipping", path: "/admin/users" },
      // Hub Empresas (ex "Contratos"): absorbe Prospectos como su tab Pipeline
      // (/admin/prospects redirige a /admin/contracts?tab=pipeline).
      { label: "Empresas", icon: "domain", path: "/admin/contracts" },
    ],
  },
  {
    title: "SOPORTE",
    icon: "support_agent",
    items: [
      ...ticketsMenuItem,
      // Transicional: la tickera vieja sigue siendo la puerta de apelaciones y
      // de los brazos SAC/Administración/Supervisión hasta migrar.
      { label: "Incidencias", icon: "report", path: "/admin/incidents" },
      { label: "Mesa de ayuda", icon: "support_agent", path: "/admin/helpdesk" },
      ...helpdeskIncidentsMenuItem,
      { label: "Centro de alertas", icon: "notifications_active", path: "/admin/notifications" },
    ],
  },
  // PLATAFORMA — todo lo que solo ve el superadmin del sistema, integrado al
  // sidebar único del portal (el área /plataforma/* ya no trae shell propio;
  // ver PlatformLayout). Los grupos espejan las secciones del mockup mu-portal
  // y `badge: "MU"` marca los ítems que siguen siendo maqueta portada: la
  // marca se quita ítem a ítem a medida que cada vista se sanea. El superadmin
  // ATERRIZA en /plataforma al iniciar sesión (ver loginLayout).
  ...(role === roleRoutes.systemSuperadmin
    ? [
        {
          title: "PLATAFORMA",
          icon: "shield_person",
          items: [
            { label: "Overview", icon: "space_dashboard", path: "/plataforma", badge: "MU" },
            { label: "Crecimiento", icon: "trending_up", path: "/plataforma/analitica", badge: "MU" },
            // IA y Almacenamiento son herramientas de sistema reales (sin MU):
            // viven acá con el resto de lo que solo ve el superadmin.
            { label: "Inteligencia artificial", icon: "smart_toy", path: "/admin/ia-config" },
            { label: "Almacenamiento", icon: "database", path: "/admin/almacenamiento" },
          ],
        },
        {
          title: "CLIENTES",
          icon: "domain",
          items: [
            { label: "Tenants", icon: "domain", path: "/plataforma/tenants", badge: "MU" },
            { label: "Usuarios", icon: "group", path: "/plataforma/usuarios", badge: "MU" },
            { label: "Onboarding", icon: "person_add", path: "/plataforma/onboarding", badge: "MU" },
          ],
        },
        {
          title: "MONETIZACIÓN",
          icon: "paid",
          items: [
            { label: "Planes y precios", icon: "sell", path: "/plataforma/planes", badge: "MU" },
            { label: "Cupones", icon: "confirmation_number", path: "/plataforma/cupones", badge: "MU" },
            { label: "Billing global", icon: "payments", path: "/plataforma/billing", badge: "MU" },
            // Piloto: módulo de facturación SII del courier (mockup completo) en
            // prueba SOLO para el superadmin antes de abrirlo al portal operador.
            { label: "Facturación SII", icon: "receipt", path: "/plataforma/facturacion", badge: "MU" },
            { label: "Uso y consumo", icon: "speed", path: "/plataforma/uso", badge: "MU" },
          ],
        },
        {
          title: "PRODUCTO",
          icon: "widgets",
          items: [
            { label: "Feature flags", icon: "toggle_on", path: "/plataforma/feature-flags", badge: "MU" },
            { label: "Releases", icon: "rocket_launch", path: "/plataforma/releases", badge: "MU" },
            { label: "Anuncios", icon: "campaign", path: "/plataforma/anuncios", badge: "MU" },
            // Sin badge MU: pop-ups reales a los comercios (contracts/announcements).
            { label: "Pop-ups a comercios", icon: "web_asset", path: "/plataforma/popups" },
            // Sin badge MU: novedades reales en la campana del comercio (contracts/product-news).
            { label: "Novedades para comercios", icon: "auto_awesome", path: "/plataforma/novedades" },
            // Sin badge MU: campaña real que ven los destinatarios en el tracking público.
            { label: "Banner del tracking", icon: "campaign", path: "/plataforma/banner-tracking" },
            // Sin badge MU: no es maqueta, lee y escribe la configuración real
            // de microservice-billing (validación con IA de los documentos de
            // pago a repartidores, operador por operador).
            { label: "Validación IA de documentos", icon: "document_scanner", path: "/plataforma/validacion-ia" },
            // Sin badge MU: lee y escribe la configuración real de
            // microservice-users (qué herramientas tiene habilitadas cada
            // empresa logística y cada comercio; hoy, el Rutificador).
            { label: "Módulos por empresa", icon: "toggle_on", path: "/plataforma/modulos" },
          ],
        },
        {
          title: "SALUD",
          icon: "monitor_heart",
          items: [
            { label: "Salud del sistema", icon: "monitor_heart", path: "/plataforma/salud", badge: "MU" },
            { label: "Incidentes", icon: "warning", path: "/plataforma/incidentes", badge: "MU" },
            { label: "Soporte SaaS", icon: "headset_mic", path: "/plataforma/soporte", badge: "MU" },
            { label: "Comunas no mapeadas", icon: "wrong_location", path: "/plataforma/comunas" },
            // Sin badge MU: lee la colección `orders` real (ventas notificadas por
            // las plataformas de e-commerce, hoy MercadoLibre) de todos los comercios.
            { label: "Órdenes e-commerce", icon: "shopping_bag", path: "/plataforma/ordenes" },
            // Sin badge MU: es real. Va junto a Incidentes porque su uso es
            // avisar a los destinatarios de una contingencia operativa.
            { label: "Avisos por WhatsApp", icon: "chat", path: "/plataforma/whatsapp-plantillas" },
          ],
        },
        {
          title: "GOBERNANZA",
          icon: "policy",
          items: [
            { label: "Auditoría", icon: "fact_check", path: "/plataforma/auditoria", badge: "MU" },
            { label: "Seguridad", icon: "security", path: "/plataforma/seguridad", badge: "MU" },
            { label: "Equipo Beartrack", icon: "diversity_3", path: "/plataforma/equipo", badge: "MU" },
            { label: "Webhooks", icon: "webhook", path: "/plataforma/webhooks" },
            { label: "Configuración", icon: "settings", path: "/plataforma/configuracion", badge: "MU" },
          ],
        },
      ]
    : []),
  // FLOTA — control de la flota propia y su hardware GPS (monitor en vivo,
  // inventario HW, geocercas y alarmas). Se oculta del sidebar; las rutas
  // (/admin/gps-*) siguen existiendo, pero no se surfacean en el menú.
  // {
  //   title: "FLOTA",
  //   icon: "local_shipping",
  //   items: [
  //     { label: "Monitoreo GPS", icon: "gps_fixed", path: "/admin/gps-monitoreo" },
  //     { label: "Dispositivos GPS", icon: "device_hub", path: "/admin/gps-dispositivos" },
  //     { label: "Geocercas", icon: "polyline", path: "/admin/gps-geocercas" },
  //     { label: "Alarmas GPS", icon: "notifications_active", path: "/admin/gps-alarmas" },
  //   ],
  // },
  // "Config. BSale" (/admin/billing/settings) y "Preguntas frecuentes"
  // (/admin/faqs) se movieron a Configuración de empresa (/admin/settings,
  // accesible desde el menú de usuario) como pestañas; ya no van en el sidebar.
  // CONFIGURACIÓN (Empresa → /admin/settings) se movió al menú de usuario del
  // header (UserMenu) junto a "Mi cuenta", consolidando cuenta + empresa.
  // "Administrar flota" (/admin/adminFleet) y "Rutas optimizadas"
  // (/admin/optimized-routes) se quitaron del menú: rotas en el backend
  // (GET /fleet devuelve 502/timeout) hasta que se rehaga ese servicio.
];

// Secciones del portal de operación ya filtradas por rol. El superadmin del
// sistema recibe el catálogo sin las vistas de operación diaria; las secciones
// que quedan sin ítems se descartan (el sidebar renderiza el encabezado aunque
// no tenga hijos, ver CollapsibleSidebar).
const getAdminSections = (role, options = {}) => {
  const sections = buildAdminSections(role, tenantModulesOf(options, "logistic"));

  if (role !== roleRoutes.systemSuperadmin) return sections;

  return sections
    .map((section) => ({
      ...section,
      // Igualdad exacta de path: con startsWith, ocultar "/admin/users"
      // arrastraría rutas hermanas que no están en la lista.
      items: section.items.filter((item) => !RUTAS_OCULTAS_SYSTEM_SUPERADMIN.has(item.path)),
    }))
    .filter((section) => section.items.length > 0);
};

const getClientSections = (options = {}) => {
  const sections = [
    {
      title: "PEDIDOS",
      icon: "package_2",
      items: [
        { label: "Resumen", icon: "home", path: "/home" },
        // Espejo de la entrada del operador. El comercio es quien cargó la
        // dirección y quien mejor puede corregirla, y el backend acota la
        // bandeja a sus propios pedidos a partir del claim.
        {
          label: "Direcciones por resolver",
          icon: "wrong_location",
          path: "/home/direcciones-por-resolver",
          badgeKey: "pendingAddresses",
        },
        // Solo si la plataforma le habilitó el módulo a este comercio. "Beta"
        // por lo mismo que en el portal de operación: todavía no crea rutas en
        // el backend. El acceso lo controla RequireTenantModule (routes.jsx).
        ...(tenantModulesOf(options, "company").includes("rutificador")
          ? [{ label: "Rutificador", icon: "route", path: "/home/rutificador", badge: "Beta" }]
          : []),
      ],
    },
    {
      title: "INFORMES",
      icon: "description",
      items: [
        // "bar_chart" ya está mapeado a lucide BarChart3 en sidebarIcons, así
        // que el ítem no necesita tocar ese mapa compartido.
        { label: "Indicadores", icon: "bar_chart", path: "/home/indicadores" },
        { label: "Crear informe", icon: "add_chart", path: "/home/my-report" },
      ],
    },
    {
      title: "PAGOS",
      icon: "payments",
      items: [
        { label: "Pagos", icon: "payments", path: "/home/pagos" },
      ],
    },
  ];

  if (options.hasOwnFleet) {
    sections.push({
      title: "MI FLOTA",
      icon: "local_shipping",
      items: [
        { label: "Seguimiento en vivo", icon: "sensors", path: "/home/live-tracking" },
        { label: "Repartidores", icon: "people", path: "/home/fleet" },
        { label: "En vivo", icon: "location_on", path: "/home/live-routes" },
      ],
    });
  }

  sections.push({
    title: "EQUIPO",
    icon: "group",
    items: [
      { label: "Usuarios", icon: "manage_accounts", path: "/home/usuarios" },
    ],
  });

  sections.push({
    title: "ADICIONALES",
    icon: "add_circle",
    items: [
      { label: "Integraciones", icon: "integration_instructions", path: "/home/integrations" },
      { label: "Preguntas frecuentes", icon: "help", path: "/home/faqs" },
    ],
  });

  return sections;
};

const getSupervisorSections = () => [
  {
    title: "OPERACIÓN",
    icon: "sync_alt",
    items: [
      { label: "Recolecciones", icon: "event_repeat", path: "/admin/recolecciones" },
      { label: "Retiros realizados", icon: "check_circle", path: "/admin/pickupsView" },
    ],
  },
  {
    title: "SEGUIMIENTO",
    icon: "my_location",
    items: [
      { label: "Seguimiento en vivo", icon: "sensors", path: "/admin/live-tracking" },
      { label: "Monitoreo GPS", icon: "gps_fixed", path: "/admin/gps-tracking" },
      { label: "En vivo", icon: "location_on", path: "/admin/live-routes" },
    ],
  },
  {
    title: "SOPORTE",
    icon: "support_agent",
    items: [
      { label: "Mesa de ayuda", icon: "support_agent", path: "/admin/helpdesk" },
      ...helpdeskIncidentsMenuItem,
    ],
  },
];

// Sidebar unificado para soporte (mismas áreas que tenía disponibles).
// selectedApp se ignora — firma conservada por compatibilidad.
const getSupportSections = () => [
  {
    title: "OPERACIÓN",
    icon: "package_2",
    items: [
      { label: "Dashboard", icon: "space_dashboard", path: "/admin/dashboard" },
      { label: "Pedidos", icon: "list_alt", path: "/admin/pedidos" },
      { label: "Torre de control", icon: "dvr", path: "/admin/torre-control" },
      { label: "Recolecciones", icon: "event_repeat", path: "/admin/recolecciones" },
      { label: "Retiros realizados", icon: "check_circle", path: "/admin/pickupsView" },
    ],
  },
  {
    title: "SEGUIMIENTO",
    icon: "my_location",
    items: [
      { label: "Seguimiento en vivo", icon: "sensors", path: "/admin/live-tracking" },
      { label: "Monitoreo GPS", icon: "gps_fixed", path: "/admin/gps-tracking" },
      { label: "En vivo", icon: "location_on", path: "/admin/live-routes" },
    ],
  },
  {
    title: "ANÁLISIS",
    icon: "bar_chart",
    items: [
      { label: "Forecasting", icon: "trending_up", path: "/admin/forecasting" },
      { label: "Crear informe", icon: "add_chart", path: "/admin/inform" },
    ],
  },
  {
    title: "GESTIÓN",
    icon: "groups",
    items: [
      { label: "Usuarios", icon: "local_shipping", path: "/admin/users" },
    ],
  },
  {
    title: "SOPORTE",
    icon: "support_agent",
    items: [
      ...ticketsMenuItem,
      // Transicional: la tickera vieja sigue siendo la puerta de apelaciones y
      // de los brazos SAC/Administración/Supervisión hasta migrar.
      { label: "Incidencias", icon: "report", path: "/admin/incidents" },
      { label: "Mesa de ayuda", icon: "support_agent", path: "/admin/helpdesk" },
      ...helpdeskIncidentsMenuItem,
      { label: "Centro de alertas", icon: "notifications_active", path: "/admin/notifications" },
      { label: "Empresas", icon: "domain", path: "/admin/contracts" },
    ],
  },
];

const getHoldingSections = () => [
  {
    title: "OPERACIÓN",
    icon: "package_2",
    items: [
      { label: "Paquetes", icon: "package_2", path: "/holding/packages" },
      { label: "Retiros", icon: "sync_alt", path: "/holding/pickups" },
    ],
  },
  {
    title: "INFORMES",
    icon: "description",
    items: [
      { label: "Crear informe", icon: "add_chart", path: "/holding/inform" },
    ],
  },
  {
    title: "GESTIÓN",
    icon: "store",
    items: [
      { label: "Empresas", icon: "store", path: "/holding/companies" },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Exportación principal
// ─────────────────────────────────────────────────────────────────────────────

// `options`: `hasOwnFleet` (comercio con flota propia), `tenantModules` (claves
// de los módulos habilitados para la empresa) y `tenantType` (de qué tipo de
// empresa son esos módulos), los dos de hooks/useTenantModules.
export const getMenuSections = (role, selectedApp, options = {}) => {
  if (role === roleRoutes.admin || role === roleRoutes.superadmin || role === roleRoutes.systemSuperadmin) {
    return getAdminSections(role, options);
  }
  if (role === roleRoutes.client || role === CLIENT_MANAGER_ROLE_ID) {
    return getClientSections(options);
  }
  if (role === roleRoutes.supervisor) {
    return getSupervisorSections();
  }
  if (role === roleRoutes.support) {
    return getSupportSections(selectedApp);
  }
  if (role === roleRoutes.holdingAdmin) {
    return getHoldingSections();
  }
  return [];
};

// ─────────────────────────────────────────────────────────────────────────────
// Bottom navigation items por rol — máximo 4 atajos.
// El 5to slot lo añade BottomNavigation como botón "Más" que abre el drawer
// con el menú completo. Por eso aquí ya no incluimos "Perfil" (la cuenta es
// accesible desde el drawer y desde el menú de usuario del header).
// ─────────────────────────────────────────────────────────────────────────────

export const getBottomNavItems = (role) => {
  // El superadmin del sistema tiene su propio set: los atajos del portal de
  // operación (En vivo, Recolecciones, Alertas) apuntaban a vistas que ya no
  // están en su menú, e "Inicio" (/admin) aterriza en el Dashboard, que
  // tampoco es suyo. Su casa es la plataforma.
  if (role === roleRoutes.systemSuperadmin) {
    return [
      { label: "Pedidos", icon: "list_alt", path: "/admin/pedidos" },
      { label: "Forecasting", icon: "trending_up", path: "/admin/forecasting" },
      { label: "Plataforma", icon: "shield_person", path: "/plataforma" },
    ];
  }
  if (role === roleRoutes.admin || role === roleRoutes.superadmin) {
    return [
      { label: "Inicio", icon: "home", path: "/admin" },
      { label: "En vivo", icon: "location_on", path: "/admin/live-routes" },
      { label: "Recolecciones", icon: "event_repeat", path: "/admin/recolecciones" },
      { label: "Alertas", icon: "notifications", path: "/admin/notifications" },
    ];
  }
  if (role === roleRoutes.client || role === CLIENT_MANAGER_ROLE_ID) {
    return [
      { label: "Inicio", icon: "home", path: "/home" },
      { label: "Informes", icon: "description", path: "/home/my-report" },
      { label: "Integrar", icon: "integration_instructions", path: "/home/integrations" },
    ];
  }
  if (role === roleRoutes.supervisor) {
    return [
      { label: "En vivo", icon: "location_on", path: "/admin/live-routes" },
      { label: "Recolecciones", icon: "event_repeat", path: "/admin/recolecciones" },
      { label: "Ayuda", icon: "support_agent", path: "/admin/helpdesk" },
    ];
  }
  if (role === roleRoutes.support) {
    return [
      { label: "Pedidos", icon: "package_2", path: "/admin/pedidos" },
      { label: "Ayuda", icon: "support_agent", path: "/admin/helpdesk" },
      ...ticketsMenuItem,
      // Transicional: la tickera vieja sigue siendo la puerta de apelaciones y
      // de los brazos SAC/Administración/Supervisión hasta migrar.
      { label: "Incidencias", icon: "report", path: "/admin/incidents" },
      { label: "Alertas", icon: "notifications", path: "/admin/notifications" },
    ];
  }
  if (role === roleRoutes.holdingAdmin) {
    return [
      { label: "Paquetes", icon: "package_2", path: "/holding/packages" },
      { label: "Retiros", icon: "sync_alt", path: "/holding/pickups" },
      { label: "Empresas", icon: "store", path: "/holding/companies" },
    ];
  }
  return [];
};
